import React, { Suspense, useCallback, useState } from 'react';
import { FormProvider, useForm, useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputProps,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { FloatingInputField, RequiredStyledProvider } from '@payler/ui-components';
import { TextStyles } from '@payler/ui-theme';
import { ApiErrorText } from '../../components/ApiErrorText/ApiErrorText';
import { useTranslation } from 'react-i18next';
import { SelectCurrency } from '../../components/SelectContractCurrency/SelectCurrency';
import {
  TCreatePayoutRequest,
  TCurrency,
  TExtendedContract,
} from '../../../../../api/src';
import { useSelectedContract } from '../../hooks/contracts';
import { useGetAxiosError } from '../../hooks/use-get-axios-error';
import { TFunction } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';

import {
  useDropPayoutsQueryCache,
  useMakeCreditMutation,
} from '../../hooks/payouts/queries';
import { useUserPermissions } from '../../hooks/user/users';
import { useCurrencyCallbacks } from '../../hooks/currencies-info';
import { useWithdrawalStatusToast } from '../../hooks/payouts/use-withdrawal-status-toast';
import { ExpDateField } from '../../components/ExpDateField/ExpDateField';
import { CvvField } from '../../components/CvvField/CvvField';
import { PaymentPasswordField } from '../../components/PaymentPasswordField/PaymentPasswordField';
import { ObjectShape } from 'yup/lib/object';
import dayjs from 'dayjs';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';

type Props = {
  onSubmit?: VoidFunction;
};

type FormData = {
  amount: number | null;
  currency: TCurrency;
  cardNumber: string;
  password: string;
  userData?: string;
  product?: string;
  expirationDate?: string;
  secureCode?: string;
  cardHolderName?: string;
};

const useGetDefaultValues = (): (() => FormData) => {
  const contract = useSelectedContract();

  return useCallback(() => {
    const currencies = contract?.currencyCodes ?? [];
    let currency: TCurrency = 'RUB';
    if (currencies.length > 0) {
      currency = currencies.includes('RUB') ? 'RUB' : (currencies[0] as string);
    }
    return {
      amount: null,
      currency,
      cardNumber: '',
      cardHolderName: '',
      password: '',
      userData: '',
      product: '',
    };
  }, [contract]);
};

function createResolver(t: TFunction, contract?: TExtendedContract) {
  const shape: ObjectShape = {
    amount: yup
      .number()
      .typeError(t('form.invalidAmount', { ns: 'withdrawals' }))
      .required(t('form.invalidAmount', { ns: 'withdrawals' }))
      .positive(t('form.invalidAmount', { ns: 'withdrawals' })),
    password: yup
      .string()
      .required(t('form.invalidPassword', { ns: 'withdrawals' })),
    cardNumber: yup
      .string()
      .nullable()
      .required(t('validate.required', { ns: 'common' }))
      .min(16, t('form.invalidCard', { ns: 'withdrawals' })),
    userData: yup
      .string()
      .nullable()
      .max(1000, t('form.userDataMaxLength', { ns: 'withdrawals' })),
    product: yup
      .string()
      .nullable()
      .max(256, t('form.paymentDescriptionMaxLength', { ns: 'withdrawals' })),
  };
  if (contract?.octDetails?.expDateRequired) {
    shape.expirationDate = yup
      .string()
      .nullable()
      .test((value, ctx) => {
        if (!value)
          return ctx.createError({
            message: t('validate.required', { ns: 'common' }),
          });
        if (!value.match(/^\d{4}$/)) {
          return ctx.createError({
            message: t('form.invalidExpirationDate', { ns: 'withdrawals' }),
          });
        }
        const m = Number(value.slice(0, 2));
        const y = Number(value.slice(-2));
        if (m < 1 || m > 12) {
          return ctx.createError({
            message: t('form.invalidExpirationDate', { ns: 'withdrawals' }),
          });
        }
        const currentYear = dayjs().year();
        if (2000 + y < currentYear - 10 || 2000 + y > currentYear + 10) {
          return ctx.createError({
            message: t('form.invalidExpirationDate', { ns: 'withdrawals' }),
          });
        }
        return true;
      });
  }
  if (contract?.octDetails?.cvvRequired) {
    shape.secureCode = yup
      .string()
      .nullable()
      .test((value, ctx) => {
        if (!value)
          return ctx.createError({
            message: t('validate.required', { ns: 'common' }),
          });
        if (!value.match(/^\d{3}$/)) {
          return ctx.createError({
            message: t('validate.wrongFormat', { ns: 'common' }),
          });
        }
        return true;
      });
  }

  if (contract?.octDetails?.cardHolderRequired === true) {
    shape.cardHolderName = yup
      .string()
      .trim()
      .required(t('validate.required', { ns: 'common' }))
      .matches(/^[a-zA-Z .'-]+$/, {
        message: t('form.invalidCardHolderName', { ns: 'withdrawals' }),
        excludeEmptyString: true,
      })
      .test(
        'max-spaces',
        t('form.invalidCardHolderName', { ns: 'withdrawals' }),
        (value) => (value?.match(/ /g)?.length ?? 0) <= 3,
      )
      .max(30, t('form.cardHolderNameMaxLength', { ns: 'withdrawals' }));
  }

  return yupResolver(yup.object(shape));
}

export const FormComp = ({ onSubmit: onSubmitOuter }: Props) => {
  const [error, setError] = useState('');
  const contractId = useURLContractId();
  const contract = useSelectedContract();
  const getDefaultValues = useGetDefaultValues();
  const refreshPayouts = useDropPayoutsQueryCache('withdrawals');
  const makeCredit = useMakeCreditMutation();
  useWithdrawalStatusToast();
  const getError = useGetAxiosError();
  const { t } = useTranslation(['withdrawals']);
  const { t: tWithoutNs } = useTranslation();
  const methods = useForm<FormData>({
    mode: 'onTouched',
    defaultValues: getDefaultValues(),
    resolver: createResolver(tWithoutNs, contract),
  });
  const { reset } = methods;

  const onSubmit = methods.handleSubmit((values) => {
    if (!contractId) return;
    const {
      expirationDate,
      secureCode,
      cardHolderName,
      userData,
      product,
      ...commonValues
    } = values;
    setError('');

    const data: TCreatePayoutRequest = {
      contractId,
      ...commonValues,
      amount: commonValues.amount ?? 0,
    };

    if (contract?.octDetails?.cvvRequired) {
      data.secureCode = secureCode;
    }

    if (contract?.octDetails?.expDateRequired && expirationDate) {
      data.expiredMonth = Number(expirationDate.slice(0, 2));
      data.expiredYear = Number(expirationDate.slice(-2));
    }

    if (contract?.octDetails?.cardHolderRequired === true) {
      data.cardHolderName = cardHolderName;
    }

    if (userData && userData.trim()) {
      data.userData = userData.trim();
    }

    if (product && product.trim()) {
      data.product = product.trim();
    }

    makeCredit.mutate(data, {
      onError: (e: unknown) => {
        const message = getError(e);
        setError(message);
      },
      onSuccess: () => {
        reset();
        onSubmitOuter?.();
      },
      onSettled: refreshPayouts,
    });
  });
  const { canWithdraw } = useUserPermissions();
  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} onChange={() => setError('')}>
        <VStack alignItems="stretch" spacing={2}>
          <SelectCurrency name="currency" />
          <Amount />
          <FloatingInputField
            name="cardNumber"
            label={t('form.cardNumber')}
            variant="card"
            autoComplete="off"
            role="presentation"
          />
          {contract?.octDetails?.expDateRequired && (
            <ExpDateField
              name="expirationDate"
              label={t('form.expirationDate')}
              autoComplete="off"
              role="presentation"
            />
          )}
          {contract?.octDetails?.cvvRequired && (
            <CvvField
              name="secureCode"
              label="CVV"
              autoComplete="off"
              placeholder=" "
              role="presentation"
            />
          )}
          {contract?.octDetails?.cardHolderRequired === true && (
            <FloatingInputField
              name="cardHolderName"
              label={t('form.cardHolderName')}
              maxLength={30}
              autoComplete="off"
              role="presentation"
            />
          )}
          <UserDataField />
          {contract?.isProductInputEnabled && (
            <FloatingInputField
              name="product"
              label={t('form.paymentDescription')}
              maxLength={256}
            />
          )}

          <PaymentPasswordField
            name="password"
            label={t('form.password')}
          />
          {error && <ApiErrorText>{error}</ApiErrorText>}
          <Tooltip
            label={!canWithdraw ? t('form.noAccess') : ''}
            shouldWrapChildren
          >
            <Button
              type="submit"
              isLoading={makeCredit.isPending}
              disabled={!canWithdraw}
            >
              {t('form.send')}
            </Button>
          </Tooltip>
        </VStack>
      </form>
    </FormProvider>
  );
};

const Amount = () => {
  const { watch } = useFormContext<FormData>();
  const currencyValue = watch('currency');
  const { getCurrencyDecimalsCount } = useCurrencyCallbacks();
  const { getCurrencySymbol } = useCurrencyCallbacks();
  const { t } = useTranslation(['withdrawals']);
  return (
    <FloatingInputField
      currencySuffix={getCurrencySymbol(currencyValue)}
      variant="currency"
      name="amount"
      currencyDecimals={getCurrencyDecimalsCount(currencyValue)}
      label={t('withdrawals:form.amount', {
        currency: getCurrencySymbol(currencyValue),
      })}
      autoFocus
    />
  );
};

const UserDataField = () => {
  const { t } = useTranslation(['withdrawals']);
  return (
    <Box position="relative">
      <FloatingInputField
        name="userData"
        label={t('form.userData')}
        maxLength={1000}
        pr="28px"
      />
      <Box
        position="absolute"
        right="8px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={1}
        pointerEvents="auto"
        mt="-12px"
      >
        <Tooltip
          label={t('form.userDataHelp')}
          placement="top"
          borderRadius={0.5}
          backdropFilter="auto"
          backdropBlur={1}
          color="secondary.500"
          bg="primary.350"
          padding={1}
          shouldWrapChildren
        >
          <Icon as={InfoIcon} w="14px" h="14px" color="primary.400" cursor="help" />
        </Tooltip>
      </Box>
    </Box>
  );
};

export const WithdrawForm = (props: Props) => {
  return (
    <Suspense fallback={null}>
      <FormComp {...props} />
    </Suspense>
  );
};
